
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Form, FormField, FormItem, FormLabel, FormControl } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

interface DeveloperLoginFormValues {
  email: string;
  password: string;
}

const DeveloperLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const form = useForm<DeveloperLoginFormValues>({
    defaultValues: {
      email: "",
      password: ""
    }
  });
  
  useEffect(() => {
    // Log that the developer login component has mounted
    console.log('DeveloperLogin component mounted', {
      hostname: window.location.hostname,
      pathname: window.location.pathname
    });
    
    return () => {
      console.log('DeveloperLogin component unmounted');
    };
  }, []);

  const handleDeveloperLogin = async (values: DeveloperLoginFormValues) => {
    try {
      setIsLoading(true);
      console.log("Initiating developer login with email:", values.email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });

      console.log("Developer login response:", { success: !!data.user, error: !!error });

      if (error) {
        console.error("Developer login error:", error);
        toast.error(
          "Utvecklarinloggning misslyckades. Kontrollera dina uppgifter.",
          { duration: 4000 }
        );
        throw error;
      }

      if (data.user) {
        toast.success("Utvecklarinloggning lyckades!", { duration: 2000 });
        console.log("Login successful, redirecting to home page");
        navigate('/', { replace: true });
      }
    } catch (error: any) {
      console.error('Developer login error details:', error);
      // Error is already handled by toast above
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md space-y-4 mt-6 pt-6 border-t border-gray-200">
      <h3 className="text-lg font-medium text-center">Utvecklarinloggning</h3>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleDeveloperLogin)} className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input 
                    {...field}
                    type="email"
                    placeholder="din@email.com"
                    autoComplete="email"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Lösenord</FormLabel>
                <FormControl>
                  <Input 
                    {...field}
                    type="password" 
                    autoComplete="current-password"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          
          <Button
            type="submit"
            className="w-full bg-gray-800 hover:bg-gray-900 text-white"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                Loggar in...
              </>
            ) : (
              "Logga in som utvecklare"
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default DeveloperLogin;
